import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Plus, X, Search, Clock, Trash2, AlertTriangle } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const STATUSES = ["all", "booked", "arrived", "completed", "cancelled", "no-show"];
const STATUS_BADGE = {
  booked: "bg-[#1A1B1A]/[0.05] text-[#757675]", arrived: "bg-amber-50 text-amber-600",
  waiting: "bg-amber-100 text-amber-700", "in-session": "bg-[#205B5A]/10 text-[#205B5A]",
  completed: "bg-[#205B5A]/10 text-[#205B5A]", cancelled: "bg-red-50 text-red-500",
  "no-show": "bg-red-50 text-red-400",
};

export default function AppointmentsPage() {
  const [tab, setTab] = useState("appointments");
  const [appointments, setAppointments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [waitlist, setWaitlist] = useState([]);
  const [showWaitlistAdd, setShowWaitlistAdd] = useState(false);
  const [patients, setPatients] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [wlForm, setWlForm] = useState({ patient_id: "", appointment_type_name: "", preferred_practitioner_id: "", urgency: "normal", notes: "" });
  const [patientSearch, setPatientSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const practitionerFilter = params.get("practitioner_id") || "";
  const needsReview = params.get("needs_review") === "1";

  const loadAppts = async () => {
    setLoading(true);
    const q = new URLSearchParams({ limit: "200" });
    if (filter !== "all") q.set("status", filter);
    if (practitionerFilter) q.set("practitioner_id", practitionerFilter);
    if (needsReview) q.set("needs_review", "true");
    try { const { data } = await api.get(`/appointments?${q}`); setAppointments(data.appointments); setTotal(data.total); } catch {}
    setLoading(false);
  };

  const clearReviewFilter = () => {
    const next = new URLSearchParams(params);
    next.delete("practitioner_id");
    next.delete("needs_review");
    setParams(next, { replace: true });
  };

  const loadWaitlist = async () => {
    try { const { data } = await api.get("/waitlist"); setWaitlist(data.entries); } catch {}
  };

  useEffect(() => { loadAppts(); loadWaitlist(); }, [filter, practitionerFilter, needsReview]);

  const removeWl = async (id) => { await api.delete(`/waitlist/${id}`); loadWaitlist(); };

  const openWlAdd = async () => {
    const [tRes, atRes] = await Promise.all([api.get("/practitioners"), api.get("/appointment-types")]);
    setPractitioners(tRes.data.practitioners || []);
    setShowWaitlistAdd(true);
  };

  const searchPat = async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    try { const { data } = await api.get(`/patients?search=${q}&limit=5`); setSearchResults(data.patients); } catch {}
  };

  useEffect(() => { const t = setTimeout(() => searchPat(patientSearch), 300); return () => clearTimeout(t); }, [patientSearch]);

  const addToWaitlist = async (e) => {
    e.preventDefault();
    try { await api.post("/waitlist", wlForm); setShowWaitlistAdd(false); loadWaitlist(); } catch {}
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="appointments-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-[#1A1B1A]" style={H}>Appointments</h1>
          <p className="text-sm text-[#757675]">{total} total</p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "waitlist" ? (
            <button onClick={openWlAdd} data-testid="add-waitlist-btn" className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#174544] flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add to wait list</button>
          ) : (
            <button onClick={() => navigate("/app/calendar")} data-testid="goto-calendar-btn" className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#174544] flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Book appointment</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-[#1A1B1A]/[0.06]">
        <button onClick={() => setTab("appointments")} data-testid="tab-appointments"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "appointments" ? "text-[#205B5A] border-[#205B5A]" : "text-[#757675] border-transparent hover:text-[#1A1B1A]"}`}>
          Appointments
        </button>
        <button onClick={() => setTab("waitlist")} data-testid="tab-waitlist"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === "waitlist" ? "text-[#205B5A] border-[#205B5A]" : "text-[#757675] border-transparent hover:text-[#1A1B1A]"}`}>
          Wait list
          {waitlist.length > 0 && <span className="text-[10px] font-semibold bg-[#205B5A]/10 text-[#205B5A] px-1.5 py-0.5 rounded-full">{waitlist.length}</span>}
        </button>
      </div>

      {tab === "appointments" && (
        <>
          {(needsReview || practitionerFilter) && (
            <div className="mb-4 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3"
              data-testid="review-filter-banner">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <p className="text-xs text-amber-700 flex-1">
                {needsReview
                  ? "Showing appointments that need review because their practitioner is no longer active. Reassign or cancel each one."
                  : "Showing appointments for one practitioner only."}
              </p>
              <button onClick={clearReviewFilter} data-testid="clear-review-filter-btn"
                className="text-[11px] font-medium text-amber-800 hover:underline">Show all</button>
            </div>
          )}
          <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
            {STATUSES.map(s => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
                className={`px-3 py-1.5 rounded-full text-[11px] font-medium capitalize whitespace-nowrap transition-colors ${filter === s ? "bg-[#205B5A] text-white" : "bg-white text-[#757675] border border-[#1A1B1A]/10 hover:border-[#1A1B1A]/20"}`}>{s === "in-session" ? "In session" : s}</button>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-[#1A1B1A]/[0.06]">
                <th className="text-left px-5 py-3 text-xs font-medium text-[#757675] uppercase">Date</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#757675] uppercase">Time</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#757675] uppercase">Patient</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#757675] uppercase hidden md:table-cell">Practitioner</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#757675] uppercase hidden lg:table-cell">Type</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-[#757675] uppercase">Status</th>
              </tr></thead>
              <tbody>
                {appointments.map(a => (
                  <tr key={a.id} className="border-b border-[#1A1B1A]/[0.03] hover:bg-[#FAF9F6] cursor-pointer" onClick={() => navigate(`/app/appointments/${a.id}`)}>
                    <td className="px-5 py-3 text-sm text-[#1A1B1A]">{a.date}</td>
                    <td className="px-5 py-3 text-sm text-[#757675] font-mono">{a.start_time}</td>
                    <td className="px-5 py-3 text-sm font-medium text-[#1A1B1A]">{a.patient_name}</td>
                    <td className="px-5 py-3 text-sm text-[#757675] hidden md:table-cell">
                      {a.practitioner_name}
                      {a.needs_practitioner_review && (
                        <span className="ml-2 text-[9px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700"
                          data-testid={`needs-review-${a.id}`}>Needs review</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-sm text-[#757675] hidden lg:table-cell">{a.appointment_type_name}</td>
                    <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_BADGE[a.status] || "bg-[#1A1B1A]/[0.04] text-[#757675]"}`}>{a.status}</span></td>
                  </tr>
                ))}
                {appointments.length === 0 && !loading && <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-[#757675]">No appointments found</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "waitlist" && (
        <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
          {waitlist.length === 0 ? (
            <div className="p-12 text-center">
              <Clock className="w-8 h-8 text-[#757675]/30 mx-auto mb-2" />
              <p className="text-sm text-[#757675]">No patients on the wait list</p>
            </div>
          ) : (
            waitlist.map(e => (
              <div key={e.id} className="flex items-center gap-4 px-5 py-4 border-b border-[#1A1B1A]/[0.03] last:border-0">
                <div className="w-8 h-8 rounded-full bg-[#205B5A]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[9px] font-semibold text-[#205B5A]">{e.patient_name?.split(" ").map(n => n?.[0]).join("")}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1B1A]">{e.patient_name}</p>
                  <p className="text-xs text-[#757675]">{e.appointment_type_name || "Any type"}{e.practitioner_name ? ` · ${e.practitioner_name}` : ""}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${e.urgency === "high" ? "bg-red-50 text-red-500" : e.urgency === "medium" ? "bg-amber-50 text-amber-600" : "bg-[#1A1B1A]/[0.04] text-[#757675]"}`}>{e.urgency}</span>
                <button onClick={() => removeWl(e.id)} className="p-1 text-[#757675] hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add to waitlist modal */}
      {showWaitlistAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="waitlist-add-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Add to wait list</h2>
              <button onClick={() => setShowWaitlistAdd(false)}><X className="w-4 h-4 text-[#757675]" /></button>
            </div>
            <form onSubmit={addToWaitlist} className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#757675]" />
                <input value={patientSearch} onChange={e => setPatientSearch(e.target.value)} placeholder="Search patient..."
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                {searchResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded-xl shadow-lg max-h-40 overflow-y-auto">
                    {searchResults.map(p => (
                      <button type="button" key={p.id} onClick={() => { setWlForm({...wlForm, patient_id: p.id}); setPatientSearch(`${p.first_name} ${p.last_name}`); setSearchResults([]); }}
                        className="w-full text-left px-3 py-2 hover:bg-[#FAF9F6] text-sm">{p.first_name} {p.last_name}</button>
                    ))}
                  </div>
                )}
              </div>
              <select value={wlForm.urgency} onChange={e => setWlForm({...wlForm, urgency: e.target.value})}
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]">
                <option value="normal">Normal priority</option><option value="medium">Medium priority</option><option value="high">High priority</option>
              </select>
              <textarea value={wlForm.notes} onChange={e => setWlForm({...wlForm, notes: e.target.value})} rows={2} placeholder="Notes (preferred times, days, etc.)"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none" />
              <div className="flex justify-end gap-3 pt-1">
                <button type="button" onClick={() => setShowWaitlistAdd(false)} className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
                <button type="submit" disabled={!wlForm.patient_id} data-testid="confirm-waitlist-btn"
                  className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-40">Add to wait list</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
